import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, formatMoney, ROLE_LABEL } from "@/lib/api";
import { Plus, Trash2, AlertTriangle, Sparkles, Users, Calendar, Wallet, Stethoscope, Building2, Save, Pencil, KeyRound, Palette, PenLine } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import BrandingSection from "@/components/BrandingSection";
import SignaturePad from "@/components/SignaturePad";

const DAYS = [
  { key: "mon", label: "Lunes" },
  { key: "tue", label: "Martes" },
  { key: "wed", label: "Miércoles" },
  { key: "thu", label: "Jueves" },
  { key: "fri", label: "Viernes" },
  { key: "sat", label: "Sábado" },
  { key: "sun", label: "Domingo" },
];

const TABS = [
  { key: "clinic", label: "Clínica", icon: Building2 },
  { key: "branding", label: "Apariencia", icon: Palette },
  { key: "users", label: "Usuarios", icon: Users },
  { key: "treatments", label: "Tratamientos", icon: Stethoscope },
  { key: "schedule", label: "Horario", icon: Calendar },
  { key: "payments", label: "Métodos de pago", icon: Wallet },
];

export default function Ajustes() {
  const { user } = useAuth();
  const [tab, setTab] = useState("clinic");
  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6" data-testid="settings-page">
      <header>
        <div className="label-eyebrow mb-2">Configuración</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">Ajustes</h1>
        <p className="text-muted-foreground text-sm mt-3">Gestión de la clínica, equipo, catálogos y horarios.</p>
      </header>

      <div className="flex border-b border-border overflow-x-auto" data-testid="settings-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key} onClick={() => setTab(t.key)}
              data-testid={`settings-tab-${t.key}`}
              className={`px-4 sm:px-5 h-11 text-sm font-semibold whitespace-nowrap transition-colors border-b-2 -mb-px inline-flex items-center gap-2 ${
                active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "clinic" && <ClinicSection isAdmin={isAdmin} />}
      {tab === "branding" && <BrandingSection isAdmin={isAdmin} />}
      {tab === "users" && <UsersSection currentUser={user} />}
      {tab === "treatments" && <TreatmentsSection isAdmin={isAdmin} />}
      {tab === "schedule" && <ScheduleSection isAdmin={isAdmin} />}
      {tab === "payments" && <PaymentMethodsSection isAdmin={isAdmin} />}
    </div>
  );
}

// ----------------- Clínica -----------------
function ClinicSection({ isAdmin }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/clinic-settings").then((r) => setS(r.data)); }, []);

  if (!s) return <div className="text-sm text-muted-foreground py-8 text-center">Cargando…</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/clinic-settings", { name: s.name, address: s.address, phone: s.phone, email: s.email, timezone: s.timezone });
      toast.success("Datos de la clínica guardados");
      // Persist clinic name for receipts/PDFs
      if (s.name) localStorage.setItem("df_clinic_name", s.name);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <section className="card-flat p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-xl tracking-tight">Información de la clínica</h2>
        {isAdmin && (
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-clinic">
            <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar"}
          </button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">Estos datos aparecen en recibos, presupuestos y consentimientos.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label-eyebrow block mb-1">Nombre de la clínica</label>
          <input className="input-flat" disabled={!isAdmin} value={s.name || ""} onChange={(e) => setS({ ...s, name: e.target.value })} data-testid="clinic-name" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Teléfono</label>
          <input className="input-flat font-mono" disabled={!isAdmin} value={s.phone || ""} onChange={(e) => setS({ ...s, phone: e.target.value })} />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Correo</label>
          <input className="input-flat" disabled={!isAdmin} value={s.email || ""} onChange={(e) => setS({ ...s, email: e.target.value })} />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Zona horaria</label>
          <input className="input-flat font-mono" disabled value={s.timezone || "America/Bogota"} />
        </div>
        <div className="sm:col-span-2">
          <label className="label-eyebrow block mb-1">Dirección</label>
          <input className="input-flat" disabled={!isAdmin} value={s.address || ""} onChange={(e) => setS({ ...s, address: e.target.value })} />
        </div>
      </div>
    </section>
  );
}

// ----------------- Usuarios -----------------
function UsersSection({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", username: "", email: "", role: "odontologo", password: "", registry_number: "", specialty: "", phone: "", color: "#1B4332", signature_text: "" });
  const [edit, setEdit] = useState(null);
  const [editingSignature, setEditingSignature] = useState(false);
  const isAdmin = currentUser?.role === "admin";

  const load = async () => { const { data } = await api.get("/users"); setUsers(data); };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Usuario creado");
      setForm({ name: "", username: "", email: "", role: "odontologo", password: "", registry_number: "", specialty: "", phone: "", color: "#1B4332", signature_text: "" });
      setAdding(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveEdit = async () => {
    try {
      const payload = {
        name: edit.name, role: edit.role, username: edit.username, email: edit.email || null,
        registry_number: edit.registry_number || "", specialty: edit.specialty || "",
        phone: edit.phone || "", color: edit.color || "#1B4332", signature_text: edit.signature_text || "",
        signature_image: edit.signature_image || "",
      };
      if (edit.newPassword) payload.password = edit.newPassword;
      await api.put(`/users/${edit.id}`, payload);
      toast.success("Usuario actualizado");
      setEdit(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Eliminado"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const isDoctor = (r) => r === "odontologo";
  const newIsDoctor = isDoctor(form.role);
  const editIsDoctor = isDoctor(edit?.role);

  return (
    <section className="card-flat p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display font-bold text-xl tracking-tight">Equipo de la clínica</h2>
          <p className="text-xs text-muted-foreground">Administra usuarios con login y los datos profesionales de los odontólogos · {users.length}/10</p>
        </div>
        {isAdmin && users.length < 10 && (
          <button onClick={() => setAdding((v) => !v)} className="btn-primary" data-testid="add-user-btn">
            <Plus className="w-4 h-4" /> {adding ? "Cancelar" : "Nuevo usuario"}
          </button>
        )}
      </div>

      {adding && (
        <form onSubmit={add} className="border border-border rounded-sm p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 bg-muted/30" data-testid="user-form">
          <input required placeholder="Nombre completo (Dra. María Pérez)" className="input-flat" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
          <input required placeholder="Usuario (login) — ej. maria.perez" className="input-flat font-mono" value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, "") })}
            autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="user-username" />
          <input type="email" placeholder="Correo (opcional, para notificaciones)" className="input-flat" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" />
          <select className="input-flat" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role">
            <option value="admin">Administrador</option>
            <option value="odontologo">Odontólogo</option>
            <option value="recepcionista">Recepcionista</option>
          </select>
          <input required type="text" placeholder="Contraseña (mín 6)" className="input-flat font-mono" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
          {newIsDoctor && (
            <>
              <input placeholder="Registro profesional (ej. RM 12345)" className="input-flat font-mono" value={form.registry_number} onChange={(e) => setForm({ ...form, registry_number: e.target.value })} data-testid="user-registry" />
              <input placeholder="Especialidad (Endodoncista, Ortodoncista…)" className="input-flat" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} data-testid="user-specialty" />
              <input placeholder="Teléfono profesional" className="input-flat font-mono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <div className="flex items-center gap-2">
                <span className="label-eyebrow">Color en agenda</span>
                <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-9 h-9 rounded-sm border-0 p-0 cursor-pointer" />
              </div>
              <input placeholder="Texto bajo la firma en PDFs (opcional)" className="input-flat sm:col-span-2" value={form.signature_text} onChange={(e) => setForm({ ...form, signature_text: e.target.value })} />
            </>
          )}
          <div className="sm:col-span-2 flex gap-2">
            <button type="submit" className="btn-primary" data-testid="user-save">
              <Plus className="w-4 h-4" /> Crear
            </button>
          </div>
        </form>
      )}

      <div className="overflow-hidden border border-border rounded-sm">
        <div className="grid grid-cols-12 px-4 py-3 border-b border-border bg-muted/40 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
          <div className="col-span-3">Nombre</div>
          <div className="col-span-3">Datos profesionales</div>
          <div className="col-span-3">Usuario / Correo</div>
          <div className="col-span-1">Rol</div>
          <div className="col-span-2 text-right">Acciones</div>
        </div>
        {users.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">Sin usuarios.</div>
        ) : users.map((u) => (
          <div key={u.id} className="grid grid-cols-12 px-4 py-3 border-b border-border last:border-b-0 items-center" data-testid={`user-row-${u.id}`}>
            <div className="col-span-3 flex items-center gap-2 min-w-0">
              {u.color && <div className="w-2.5 h-8 rounded-sm shrink-0" style={{ background: u.color }} />}
              <div className="min-w-0">
                <div className="text-sm font-bold truncate">{u.name}</div>
                {u.specialty && <div className="text-[10px] text-muted-foreground truncate">{u.specialty}</div>}
              </div>
            </div>
            <div className="col-span-3 text-xs">
              {u.registry_number ? (
                <div className="font-mono text-foreground/80">{u.registry_number}</div>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
              {u.phone && <div className="text-muted-foreground font-mono text-[10px]">{u.phone}</div>}
            </div>
            <div className="col-span-3 text-xs truncate">
              <div className="font-mono font-bold text-foreground">{u.username || "—"}</div>
              {u.email && <div className="text-muted-foreground truncate">{u.email}</div>}
            </div>
            <div className="col-span-1">
              <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm bg-primary/15 text-primary">
                {(ROLE_LABEL[u.role] || u.role).slice(0, 4)}
              </span>
            </div>
            <div className="col-span-2 flex justify-end items-center gap-1">
              {isAdmin && (
                <>
                  <button onClick={() => { setEditingSignature(false); setEdit({ ...u, newPassword: "" }); }} className="p-2 text-muted-foreground hover:text-primary" title="Editar">
                    <Pencil className="w-4 h-4" />
                  </button>
                  {u.id !== currentUser?.id && (
                    <button onClick={() => remove(u)} className="p-2 text-muted-foreground hover:text-destructive" title="Eliminar">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEdit(null)}>
          <div className="bg-card rounded-sm border border-border w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="user-edit-dialog">
            <h3 className="font-display font-bold text-xl tracking-tight">Editar usuario</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow block mb-1">Nombre</label>
                <input className="input-flat" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Usuario (login)</label>
                <input className="input-flat font-mono" value={edit.username || ""}
                  onChange={(e) => setEdit({ ...edit, username: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, "") })}
                  autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="edit-username" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Correo (opcional)</label>
                <input type="email" className="input-flat" value={edit.email || ""} onChange={(e) => setEdit({ ...edit, email: e.target.value })} />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Rol</label>
                <select className="input-flat" value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })}>
                  <option value="admin">Administrador</option>
                  <option value="odontologo">Odontólogo</option>
                  <option value="recepcionista">Recepcionista</option>
                </select>
              </div>
              <div>
                <label className="label-eyebrow block mb-1 inline-flex items-center gap-2"><KeyRound className="w-3 h-3" /> Nueva contraseña (opcional)</label>
                <input type="text" className="input-flat font-mono" placeholder="Vacío = no cambiar" value={edit.newPassword || ""} onChange={(e) => setEdit({ ...edit, newPassword: e.target.value })} />
              </div>
              {editIsDoctor && (
                <>
                  <div className="sm:col-span-2 pt-3 border-t border-border">
                    <div className="label-eyebrow text-primary mb-2">Datos profesionales</div>
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-1">Registro profesional</label>
                    <input className="input-flat font-mono" placeholder="RM 12345" value={edit.registry_number || ""} onChange={(e) => setEdit({ ...edit, registry_number: e.target.value })} data-testid="edit-registry" />
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-1">Especialidad</label>
                    <input className="input-flat" placeholder="Endodoncista" value={edit.specialty || ""} onChange={(e) => setEdit({ ...edit, specialty: e.target.value })} />
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-1">Teléfono</label>
                    <input className="input-flat font-mono" value={edit.phone || ""} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} />
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-1">Color en agenda</label>
                    <div className="flex items-center gap-2 h-10">
                      <input type="color" value={edit.color || "#1B4332"} onChange={(e) => setEdit({ ...edit, color: e.target.value })} className="w-9 h-9 rounded-sm border-0 p-0 cursor-pointer" />
                      <span className="font-mono text-xs text-muted-foreground">{edit.color || "#1B4332"}</span>
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label-eyebrow block mb-1">Texto bajo la firma (PDFs)</label>
                    <input className="input-flat" placeholder="Ej. Tarjeta profesional N° 12345 · Universidad Nacional" value={edit.signature_text || ""} onChange={(e) => setEdit({ ...edit, signature_text: e.target.value })} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label-eyebrow block mb-1 inline-flex items-center gap-2"><PenLine className="w-3 h-3" /> Firma manuscrita</label>
                    {!editingSignature ? (
                      <div className="flex items-center gap-3 border border-border rounded-sm p-3 bg-muted/20">
                        {edit.signature_image ? (
                          <div className="bg-white rounded-sm p-2 border border-border">
                            <img src={edit.signature_image} alt="Firma" className="h-16 max-w-[220px] object-contain" />
                          </div>
                        ) : (
                          <div className="text-xs text-muted-foreground italic">Sin firma cargada.</div>
                        )}
                        <div className="ml-auto flex gap-2">
                          <button type="button" onClick={() => setEditingSignature(true)} className="px-3 h-9 rounded-sm border border-border hover:bg-muted text-xs inline-flex items-center gap-1" data-testid="edit-user-signature-btn">
                            <Pencil className="w-3 h-3" /> {edit.signature_image ? "Cambiar" : "Agregar"}
                          </button>
                          {edit.signature_image && (
                            <button type="button" onClick={() => setEdit({ ...edit, signature_image: null })} className="px-3 h-9 rounded-sm border border-destructive/40 text-destructive hover:bg-destructive/10 text-xs inline-flex items-center gap-1">
                              <Trash2 className="w-3 h-3" /> Quitar
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="border border-border rounded-sm p-3">
                        <SignaturePad
                          initialValue={edit.signature_image || null}
                          onSave={(dataUrl) => { setEdit({ ...edit, signature_image: dataUrl }); setEditingSignature(false); toast.success("Firma capturada (recuerda guardar el usuario)"); }}
                          onClear={() => { setEdit({ ...edit, signature_image: null }); setEditingSignature(false); }}
                          onCancel={() => setEditingSignature(false)}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={saveEdit} className="btn-primary flex-1 justify-center">Guardar</button>
              <button onClick={() => setEdit(null)} className="px-4 h-10 rounded-sm border border-border hover:bg-muted text-sm">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ----------------- Tratamientos -----------------
function TreatmentsSection({ isAdmin }) {
  const [treatments, setTreatments] = useState([]);
  const [t, setT] = useState({ name: "", price: "", duration_min: 30, color: "#1B4332" });
  const [editing, setEditing] = useState({});

  const load = async () => { const { data } = await api.get("/treatments"); setTreatments(data); };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/treatments", { ...t, price: Number(t.price) || 0, duration_min: Number(t.duration_min) || 30 });
      setT({ name: "", price: "", duration_min: 30, color: "#1B4332" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveEdit = async (id) => {
    const e = editing[id]; if (!e) return;
    try {
      await api.put(`/treatments/${id}`, { name: e.name, price: Number(e.price) || 0, duration_min: Number(e.duration_min) || 30, color: e.color });
      toast.success("Tratamiento actualizado");
      setEditing((s) => { const { [id]: _, ...rest } = s; return rest; });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => { if (!window.confirm("¿Eliminar tratamiento?")) return; try { await api.delete(`/treatments/${id}`); load(); } catch { toast.error("No se pudo eliminar"); } };

  const seedDefaults = async () => {
    if (!window.confirm("Se agregarán los tratamientos del catálogo por defecto que aún no tienes.")) return;
    try {
      const { data } = await api.post("/treatments/seed-defaults");
      if (data.added === 0) toast.info("Ya tienes todos los tratamientos del catálogo por defecto.");
      else toast.success(`${data.added} tratamientos añadidos`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <section className="card-flat p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display font-bold text-xl tracking-tight">Catálogo de tratamientos</h2>
          <p className="text-xs text-muted-foreground">{treatments.length} tratamientos · clic en el precio para editarlo</p>
        </div>
        {isAdmin && (
          <button onClick={seedDefaults} className="px-3 h-9 rounded-sm border border-primary/40 bg-primary/5 hover:bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2" data-testid="seed-defaults-btn">
            <Sparkles className="w-3.5 h-3.5" /> Ampliar catálogo
          </button>
        )}
      </div>

      {isAdmin && (
        <form onSubmit={add} className="grid grid-cols-2 sm:grid-cols-5 gap-3 border border-border rounded-sm p-4 bg-muted/30">
          <input required placeholder="Nombre" className="input-flat sm:col-span-2" value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} data-testid="treatment-name" />
          <input required type="number" step="1000" placeholder="Precio COP" className="input-flat font-mono" value={t.price} onChange={(e) => setT({ ...t, price: e.target.value })} data-testid="treatment-price" />
          <input type="number" placeholder="Min" className="input-flat font-mono" value={t.duration_min} onChange={(e) => setT({ ...t, duration_min: e.target.value })} />
          <button type="submit" className="btn-primary justify-center" data-testid="treatment-add"><Plus className="w-4 h-4" /> Añadir</button>
        </form>
      )}

      <div className="overflow-hidden border border-border rounded-sm">
        <div className="grid grid-cols-12 px-4 py-3 border-b border-border bg-muted/40 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
          <div className="col-span-1">Color</div>
          <div className="col-span-5">Nombre</div>
          <div className="col-span-3 text-right">Precio</div>
          <div className="col-span-2 text-right">Duración</div>
          <div className="col-span-1 text-right">—</div>
        </div>
        {treatments.map((tr) => {
          const isE = !!editing[tr.id];
          const e = editing[tr.id] || tr;
          return (
            <div key={tr.id} className="grid grid-cols-12 px-4 py-2.5 border-b border-border last:border-b-0 items-center hover:bg-muted/20" data-testid={`tr-row-${tr.id}`}>
              <div className="col-span-1">
                {isE ? (
                  <input type="color" value={e.color || "#1B4332"} onChange={(ev) => setEditing((s) => ({ ...s, [tr.id]: { ...e, color: ev.target.value } }))} className="w-7 h-7 rounded-sm border-0 p-0 cursor-pointer" />
                ) : (
                  <div className="w-5 h-5 rounded-sm" style={{ background: tr.color || "#1B4332" }} />
                )}
              </div>
              <div className="col-span-5">
                {isE ? (
                  <input className="input-flat !h-8 text-sm" value={e.name} onChange={(ev) => setEditing((s) => ({ ...s, [tr.id]: { ...e, name: ev.target.value } }))} />
                ) : (
                  <div className="text-sm font-bold truncate">{tr.name}</div>
                )}
              </div>
              <div className="col-span-3 text-right">
                {isE ? (
                  <input type="number" step="1000" className="input-flat !h-8 text-sm font-mono text-right" value={e.price} onChange={(ev) => setEditing((s) => ({ ...s, [tr.id]: { ...e, price: ev.target.value } }))} />
                ) : (
                  <button disabled={!isAdmin} onClick={() => setEditing((s) => ({ ...s, [tr.id]: { ...tr } }))} className="font-mono text-sm font-bold hover:text-primary disabled:cursor-default" data-testid={`tr-price-${tr.id}`}>
                    {formatMoney(tr.price)}
                  </button>
                )}
              </div>
              <div className="col-span-2 text-right">
                {isE ? (
                  <input type="number" className="input-flat !h-8 text-sm font-mono text-right" value={e.duration_min} onChange={(ev) => setEditing((s) => ({ ...s, [tr.id]: { ...e, duration_min: ev.target.value } }))} />
                ) : (
                  <span className="font-mono text-xs text-muted-foreground">{tr.duration_min} min</span>
                )}
              </div>
              <div className="col-span-1 flex justify-end items-center gap-1">
                {isE ? (
                  <>
                    <button onClick={() => saveEdit(tr.id)} className="p-1.5 text-primary hover:bg-primary/10 rounded-sm" title="Guardar">
                      <Save className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setEditing((s) => { const { [tr.id]: _, ...rest } = s; return rest; })} className="p-1.5 text-muted-foreground hover:bg-muted rounded-sm" title="Cancelar">
                      ✕
                    </button>
                  </>
                ) : isAdmin ? (
                  <button onClick={() => del(tr.id)} className="p-1.5 text-muted-foreground hover:text-destructive">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ----------------- Horario -----------------
function ScheduleSection({ isAdmin }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/clinic-settings").then((r) => setS(r.data)); }, []);

  if (!s) return <div className="text-sm text-muted-foreground py-8 text-center">Cargando…</div>;

  const setDay = (key, patch) => setS((prev) => ({ ...prev, schedule: { ...prev.schedule, [key]: { ...prev.schedule[key], ...patch } } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/clinic-settings", { schedule: s.schedule });
      toast.success("Horario guardado");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <section className="card-flat p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display font-bold text-xl tracking-tight">Horario de atención</h2>
          <p className="text-xs text-muted-foreground">La agenda solo permitirá citas dentro del horario configurado y los días habilitados.</p>
        </div>
        {isAdmin && (
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-schedule">
            <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar horario"}
          </button>
        )}
      </div>

      <div className="space-y-2">
        {DAYS.map((d) => {
          const day = s.schedule?.[d.key] || {};
          const enabled = !!day.enabled;
          return (
            <div key={d.key} className={`grid grid-cols-12 gap-2 items-center p-3 rounded-sm border ${enabled ? "border-primary/30 bg-primary/5" : "border-border bg-muted/20"}`} data-testid={`schedule-${d.key}`}>
              <div className="col-span-12 sm:col-span-2 flex items-center gap-2">
                <input type="checkbox" disabled={!isAdmin} checked={enabled} onChange={(e) => setDay(d.key, { enabled: e.target.checked })} className="w-4 h-4 accent-primary" data-testid={`schedule-enabled-${d.key}`} />
                <span className="font-bold text-sm">{d.label}</span>
              </div>
              <div className="col-span-3 sm:col-span-2">
                <div className="label-eyebrow mb-1">Apertura</div>
                <input type="time" disabled={!isAdmin || !enabled} className="input-flat font-mono !h-9" value={day.open || "08:00"} onChange={(e) => setDay(d.key, { open: e.target.value })} />
              </div>
              <div className="col-span-3 sm:col-span-2">
                <div className="label-eyebrow mb-1">Cierre</div>
                <input type="time" disabled={!isAdmin || !enabled} className="input-flat font-mono !h-9" value={day.close || "18:00"} onChange={(e) => setDay(d.key, { close: e.target.value })} />
              </div>
              <div className="col-span-3 sm:col-span-2">
                <div className="label-eyebrow mb-1">Almuerzo desde</div>
                <input type="time" disabled={!isAdmin || !enabled} className="input-flat font-mono !h-9" value={day.lunch_start || ""} onChange={(e) => setDay(d.key, { lunch_start: e.target.value || null })} />
              </div>
              <div className="col-span-3 sm:col-span-2">
                <div className="label-eyebrow mb-1">hasta</div>
                <input type="time" disabled={!isAdmin || !enabled} className="input-flat font-mono !h-9" value={day.lunch_end || ""} onChange={(e) => setDay(d.key, { lunch_end: e.target.value || null })} />
              </div>
              <div className="col-span-12 sm:col-span-2 text-right text-[10px] uppercase tracking-widest font-bold">
                <span className={enabled ? "text-primary" : "text-muted-foreground"}>{enabled ? "ATIENDE" : "CERRADO"}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ----------------- Métodos de pago -----------------
const COLORS_PRESET = ["#1B4332", "#DA0081", "#E1142C", "#FDDA00", "#1E3A8A", "#7A4FBA", "#D97757", "#5C605A", "#2D6A4F"];

function PaymentMethodsSection({ isAdmin }) {
  const [s, setS] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ key: "", label: "", short: "", bg: "#1B4332", fg: "#FFFFFF" });

  const load = async () => { const { data } = await api.get("/clinic-settings"); setS(data); };
  useEffect(() => { load(); }, []);

  if (!s) return <div className="text-sm text-muted-foreground py-8 text-center">Cargando…</div>;

  const persist = async (next) => {
    try {
      await api.put("/clinic-settings", { payment_methods: next });
      setS({ ...s, payment_methods: next });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const add = async (e) => {
    e.preventDefault();
    const k = form.key.toLowerCase().replace(/[^a-z0-9_-]/g, "");
    if (!k) { toast.error("Identificador inválido"); return; }
    if (s.payment_methods.find((m) => m.key === k)) { toast.error("Identificador ya existe"); return; }
    const next = [...s.payment_methods, { ...form, key: k, short: (form.short || k).toUpperCase().slice(0, 4) }];
    await persist(next);
    setForm({ key: "", label: "", short: "", bg: "#1B4332", fg: "#FFFFFF" });
    setAdding(false);
    toast.success("Método agregado");
  };

  const remove = async (key) => {
    if (!window.confirm("¿Eliminar este método de pago?")) return;
    const next = s.payment_methods.filter((m) => m.key !== key);
    await persist(next);
    toast.success("Método eliminado");
  };

  return (
    <section className="card-flat p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display font-bold text-xl tracking-tight">Métodos de pago</h2>
          <p className="text-xs text-muted-foreground">Pasarelas y formas de pago aceptadas en la clínica.</p>
        </div>
        {isAdmin && (
          <button onClick={() => setAdding((v) => !v)} className="btn-primary" data-testid="add-payment-btn">
            <Plus className="w-4 h-4" /> {adding ? "Cancelar" : "Nuevo método"}
          </button>
        )}
      </div>

      {adding && (
        <form onSubmit={add} className="border border-border rounded-sm p-4 grid grid-cols-1 sm:grid-cols-5 gap-3 bg-muted/30">
          <input required placeholder="Identificador (mercadopago)" className="input-flat font-mono sm:col-span-1" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} data-testid="pm-key" />
          <input required placeholder="Nombre visible (Mercado Pago)" className="input-flat sm:col-span-2" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="pm-label" />
          <input placeholder="Etiqueta corta (MP)" className="input-flat sm:col-span-1" maxLength={4} value={form.short} onChange={(e) => setForm({ ...form, short: e.target.value })} />
          <div className="flex gap-2 items-center">
            <input type="color" value={form.bg} onChange={(e) => setForm({ ...form, bg: e.target.value })} className="w-9 h-9 rounded-sm border-0 p-0 cursor-pointer" />
            <input type="color" value={form.fg} onChange={(e) => setForm({ ...form, fg: e.target.value })} className="w-9 h-9 rounded-sm border-0 p-0 cursor-pointer" title="Color de texto" />
          </div>
          <div className="sm:col-span-5 flex gap-2">
            <button type="submit" className="btn-primary" data-testid="pm-save">Guardar</button>
            <div className="flex gap-1 items-center">
              {COLORS_PRESET.map((c) => (
                <button key={c} type="button" onClick={() => setForm({ ...form, bg: c })} className="w-6 h-6 rounded-sm border border-border" style={{ background: c }} title={c} />
              ))}
            </div>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {s.payment_methods.map((m) => (
          <div key={m.key} className="border border-border rounded-sm p-3 flex items-center gap-3" data-testid={`pm-row-${m.key}`}>
            <div className="w-12 h-12 rounded-sm flex items-center justify-center font-bold text-[10px] uppercase tracking-widest shrink-0" style={{ background: m.bg, color: m.fg }}>
              {(m.short || m.key).slice(0, 4).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold truncate">{m.label}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{m.key}</div>
            </div>
            {isAdmin && (
              <button onClick={() => remove(m.key)} className="p-2 text-muted-foreground hover:text-destructive">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// ----------------- Danger zone (removed) -----------------
